import React from 'react';
import { Link } from 'react-router-dom'; 
import { 
  Target, Eye, ShieldCheck, HeartPulse, 
  Cpu, Award, Users, CheckCircle, Clock,
  Search, Rocket, Globe, Network
} from 'lucide-react';
import { motion } from 'framer-motion';
import FadeIn from '../components/FadeIn';

// Asset Import
import aboutImage from '../assets/images/medical-billing-services-collect.png';

const About = () => {
  const timeline = [
    { 
      icon: <Search size={24} />, 
      phase: 'Phase 1', 
      title: 'Conceptualization', 
      desc: 'Identifying the critical gap between medical billing workflows and fragmented IT systems.' 
    },
    { 
      icon: <Cpu size={24} />, 
      phase: 'Phase 2', 
      title: 'IT Integration', 
      desc: 'Developing specialized infrastructure for secure, HIPAA-compliant healthcare data management.' 
    },
    { 
      icon: <Rocket size={24} />, 
      phase: 'Phase 3', 
      title: 'Full RCM Launch', 
      desc: 'Deploying our dual-service model to optimize revenue cycles for diverse medical specialties.' 
    },
    { 
      icon: <Globe size={24} />, 
      phase: 'Phase 4', 
      title: 'Future Expansion', 
      desc: 'Continuing to modernize practice operations through AI-driven analytics and interoperability.' 
    }
  ];

  return (
    <div className="bg-white overflow-x-hidden">
      
      {/* 1. VISIONARY HERO SECTION */}
      <section className="relative py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-20 items-center">
          <FadeIn direction="right">
            <div className="relative z-10">
              <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">Our DNA</h2>
              <h1 className="text-4xl md:text-7xl font-black text-[#1e3a8a] mb-8 leading-tight">
                Modernizing <span className="text-[#14b8a6]">Healthcare</span> One Practice at a Time.
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                OptiRev Healthcare was founded on a simple premise: that financial health and technological innovation 
                should never be fragmented. We provide a unified partnership for modern providers.
              </p>
              <div className="flex gap-8">
                <div className="text-center">
                  <p className="text-3xl font-black text-[#1e3a8a]">1 Partner</p>
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">For RCM & IT</p>
                </div>
                <div className="w-[1px] h-12 bg-slate-200"></div>
                <div className="text-center">
                  <p className="text-3xl font-black text-[#14b8a6]">24/7</p>
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Expert Support</p>
                </div>
              </div>
            </div>
          </FadeIn>
          
          <FadeIn direction="left" delay={0.3}>
            <div className="relative">
              <div className="rounded-[4rem] overflow-hidden shadow-2xl border-8 border-white group">
                <img src={aboutImage} alt="Our Vision" className="w-full h-auto transition-transform duration-700 group-hover:scale-105" />
              </div>
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#14b8a6]/10 rounded-full blur-3xl -z-10"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. THE OPTIREV JOURNEY (Updated with Icons) */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn direction="down">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-bold text-[#1e3a8a]">Our Journey</h2>
              <p className="text-slate-500 mt-4">Building a bridge between finance and technology.</p>
            </div>
          </FadeIn>

          <div className="relative">
            {/* Horizontal Line for Desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-[2px] bg-slate-100 -translate-y-1/2"></div>
            
            <div className="grid lg:grid-cols-4 gap-8 relative z-10">
              {timeline.map((item, i) => (
                <FadeIn key={i} delay={i * 0.2}>
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-4 transition-all duration-500 group h-full">
                    <div className="w-16 h-16 bg-slate-50 text-[#14b8a6] rounded-2xl flex items-center justify-center mb-8 group-hover:bg-[#1e3a8a] group-hover:text-white transition-all duration-300 shadow-inner">
                      {item.icon}
                    </div>
                    <p className="text-[#14b8a6] text-xs font-black uppercase tracking-widest mb-2">{item.phase}</p>
                    <h4 className="text-xl font-bold text-[#1e3a8a] mb-4">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. MISSION & VISION SECTION */}
      <section className="py-24 px-8 bg-[#1e3a8a] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#14b8a6]/10 rounded-full blur-[120px]"></div>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 relative z-10">
          <FadeIn direction="right">
            <div className="p-12 rounded-[3rem] bg-white/5 border border-white/10 backdrop-blur-md h-full text-white">
              <Target className="text-[#14b8a6] mb-6" size={48} />
              <h3 className="text-3xl font-bold mb-4 text-white">Our Mission</h3>
              <p className="text-slate-300 leading-relaxed italic">
                "To empower healthcare providers with an integrated approach to financial and technological health."
              </p>
            </div>
          </FadeIn>
          <FadeIn direction="left" delay={0.2}>
            <div className="p-12 rounded-[3rem] bg-white/5 border border-white/10 backdrop-blur-md h-full text-white">
              <Eye className="text-[#14b8a6] mb-6" size={48} />
              <h3 className="text-3xl font-bold mb-4 text-white">Our Vision</h3>
              <p className="text-slate-300 leading-relaxed italic">
                "To set the industry standard for interoperability and revenue optimization."
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. CORE VALUES */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center mb-20">
          <FadeIn direction="down">
            <h2 className="text-4xl font-bold text-[#1e3a8a]">The OptiRev Standard</h2>
            <p className="text-slate-500 mt-4">Built on trust, security, and proven results.</p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {[
            { t: "Specialized IT", d: "Built by experts who understand billing bottlenecks.", icon: <Network className="text-[#14b8a6]" /> },
            { t: "Compliance First", d: "Strict adherence to HIPAA, HITECH, and SOC2.", icon: <ShieldCheck className="text-[#14b8a6]" /> },
            { t: "Proactive Partnership", d: "Account managers ensuring zero-bottleneck revenue.", icon: <Award className="text-[#14b8a6]" /> }
          ].map((val, i) => (
            <FadeIn key={i} delay={i * 0.2}>
              <div className="bg-slate-50 p-10 rounded-[2.5rem] border border-slate-100 hover:bg-white hover:shadow-2xl transition-all duration-500 text-center">
                <div className="mb-6 flex justify-center">{val.icon}</div>
                <h4 className="text-xl font-bold text-[#1e3a8a] mb-4">{val.t}</h4>
                <p className="text-slate-500 text-sm">{val.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* 5. FINAL TRUST BANNER */}
      <section className="py-24 bg-slate-50 border-t">
        <FadeIn direction="up">
          <div className="max-w-5xl mx-auto px-8 flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="text-center md:text-left">
              <h4 className="text-2xl font-bold text-[#1e3a8a] mb-2">Ready to meet the team?</h4>
              <p className="text-slate-500">Discover why 500+ providers trust our integrated model.</p>
            </div>
            <Link to="/contact" className="bg-[#14b8a6] text-white px-10 py-4 rounded-2xl font-bold hover:shadow-xl transition-all active:scale-95 whitespace-nowrap">
              Schedule Your Audit
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default About;